import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Ban, CalendarClock, Repeat, RotateCcw } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isAwaitingAbn, toastDraftAwaitingAbn } from "../lib/draftToast";
import { COMPLETE_UNDO_MS, undoCompleteAction, completeUndoBlockReason } from "../lib/undoComplete";
import { NEEDS_OUTCOME_CHANGED_EVENT } from "../lib/needsOutcome";
import TimeGrid from "./TimeGrid";
import { BillingChip } from "./BillingChip";
import { BOOKING_WINDOW, isOutsideWindow } from "../lib/bookingWindow";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "./ui/alert-dialog";

const fmtMel = (iso) =>
  new Date(iso).toLocaleString("en-AU", {
    timeZone: "Australia/Melbourne",
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });

// "Tue, 20 Oct 2026 · 1:00 pm" — Melbourne, same style as the rest of the app.
const fmtMelFull = (iso) => {
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-AU", {
    timeZone: "Australia/Melbourne",
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = d
    .toLocaleTimeString("en-AU", {
      timeZone: "Australia/Melbourne",
      hour: "numeric",
      minute: "2-digit",
    })
    .replace(/\u202f|\u00a0/g, " ")
    .toLowerCase();
  return `${date} · ${time}`;
};

// A double click on a session card opens this dialog under the cursor, so the
// second click can land straight on an outcome button. The outcome row stays
// inert for this long after the dialog opens.
const OUTCOME_ARM_MS = 500;

const pad = (n) => String(n).padStart(2, "0");
// Build date/time picker defaults from the session's start in the trainer's
// local clock (the app assumes Australia/Melbourne), mirroring the Book dialog.
const toDateInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toTimeInput = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// One shared selected / unselected style for BOTH reschedule toggle groups
// ("Who asked?" and "Recurring session") so each group's selection stays
// clearly and equally highlighted at the same time — picking in one group
// never dims the other's choice.
const RESCHED_SELECTED =
  "border bg-[#0F6B6B]/20 text-[#0F6B6B] border-[#0F6B6B] ring-1 ring-[#0F6B6B]/50 font-semibold";
const RESCHED_UNSELECTED =
  "border bg-[#FAF7F2] text-[#2A2623] border-[#E8E1D6] hover:border-[#0F6B6B]/40 hover:text-[#2A2623]";

// Shared across Schedule grid + client history drawer so a session behaves
// identically wherever it's opened from. Owns the "Who cancelled?" prompt,
// the Undo-after-cancel toast, and the delete confirmation. Complete can be
// overridden by the parent (Schedule injects its pack-empty warning + the
// auto-invoice toast); when no onComplete is passed we run a plain complete.
export default function SessionDetailsDialog({
  session,
  onOpenChange,
  clientName,
  invoice,
  billing,
  onComplete,
  onChanged,
}) {
  const [pendingCancel, setPendingCancel] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  // Reschedule flow state.
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedDate, setReschedDate] = useState("");
  const [reschedTime, setReschedTime] = useState("");
  const [reschedWho, setReschedWho] = useState(null); // "client" | "trainer"
  const [reschedScope, setReschedScope] = useState("single"); // "single" | "all"
  const [reschedBusy, setReschedBusy] = useState(false);
  const navigate = useNavigate();
  const { readOnly } = useAuth();
  // Outcome buttons are inert for OUTCOME_ARM_MS after the dialog opens.
  const [armed, setArmed] = useState(false);
  // Synchronous latch: two clicks in the same tick can both run their handler
  // before React commits `busy`, so the ref is what actually stops the second.
  const busyRef = useRef(false);

  const sessionId = session?.session_id;
  useEffect(() => {
    if (!sessionId) return undefined;
    setArmed(false);
    busyRef.current = false;
    setBusy(false);
    const t = setTimeout(() => setArmed(true), OUTCOME_ARM_MS);
    return () => clearTimeout(t);
  }, [sessionId]);

  if (!session) return null;
  const s = session;
  const isScheduled = s.status === "scheduled";
  const isNoShow = s.status === "no-show";
  const isCancelled = s.status === "cancelled";
  const isCompleted = s.status === "completed";
  const undoBlock = isCompleted ? completeUndoBlockReason(invoice) : null;
  const hasStarted = Date.now() >= new Date(s.starts_at).getTime();
  const hasInvoice = !!(invoice || s.invoice_id);
  const name = clientName || s.client_name || "(client removed)";
  const outcomeLocked = busy || !armed;

  const reload = async () => {
    if (onChanged) await onChanged();
    // Dashboard counts + the Schedule grid listen for this.
    window.dispatchEvent(new Event(NEEDS_OUTCOME_CHANGED_EVENT));
  };

  // Claim the one outcome action in flight. Returns false when a click should
  // be ignored (already running, or the dialog has only just opened).
  const lockOutcome = () => {
    if (busyRef.current || !armed) return false;
    busyRef.current = true;
    setBusy(true);
    return true;
  };
  const unlockOutcome = () => {
    busyRef.current = false;
    setBusy(false);
  };

  const undoNoShow = async () => {
    try {
      await api.post(`/sessions/${s.session_id}/restore`);
      toast.success("Back to scheduled — no-show removed");
      await reload();
    } catch (e) {
      toast.error(apiError(e, "Couldn't undo — please try again"));
    }
  };

  const doComplete = async () => {
    if (!lockOutcome()) return;
    if (onComplete) {
      try {
        await onComplete(s);
      } finally {
        unlockOutcome();
      }
      return;
    }
    try {
      const { data } = await api.put(`/sessions/${s.session_id}/status`, { status: "completed" });
      const inv = data?.auto_invoice;
      const undo = {
        duration: COMPLETE_UNDO_MS,
        action: undoCompleteAction(s.session_id, reload),
      };
      if (inv) {
        toast.success(`Invoice ${inv.invoice_number} (AUD $${Number(inv.total).toFixed(2)}) created`, {
          ...undo,
          description: isAwaitingAbn(inv)
            ? "Saved as a draft until your ABN is added — nothing emailed."
            : s.client_email
              ? `Sending to ${name} shortly — undo now and no email goes out.`
              : `${name} has no email on file, so nothing will be sent.`,
        });
        if (isAwaitingAbn(inv)) toastDraftAwaitingAbn(navigate);
      } else {
        toast.success("Marked completed", undo);
      }
      onOpenChange(false);
      await reload();
    } catch (e) {
      toast.error(apiError(e, "Could not mark this session complete"));
    } finally {
      unlockOutcome();
    }
  };

  // "Undo complete" once the toast has gone — allowed while the invoice is
  // unpaid and the client has not been emailed. The server enforces the same
  // rules; this only keeps the trainer from tapping a dead button.
  const doUnComplete = async () => {
    if (!lockOutcome()) return;
    try {
      const { data } = await api.post(`/sessions/${s.session_id}/restore`);
      toast.success(
        data?.invoice_removed
          ? `Back to scheduled — invoice ${data.invoice_removed} removed, no email sent`
          : "Back to scheduled — completion undone",
      );
      onOpenChange(false);
      await reload();
    } catch (e) {
      toast.error(apiError(e, "Couldn't undo this completion"));
    } finally {
      unlockOutcome();
    }
  };

  const doNoShow = async () => {
    if (!lockOutcome()) return;
    try {
      await api.put(`/sessions/${s.session_id}/status`, { status: "no-show" });
      onOpenChange(false);
      await reload();
      toast.success("Marked no-show", {
        duration: 10000,
        action: { label: "Undo", onClick: undoNoShow },
      });
    } catch (e) {
      toast.error(apiError(e, "Could not mark this session a no-show"));
    } finally {
      unlockOutcome();
    }
  };

  // "Change outcome" on a no-show class — same reversal as the Undo toast.
  const doUnNoShow = async () => {
    if (!lockOutcome()) return;
    try {
      await api.post(`/sessions/${s.session_id}/restore`);
      toast.success("Back to scheduled — no-show removed");
      onOpenChange(false);
      await reload();
    } catch (e) {
      toast.error(apiError(e, "Couldn't change the outcome"));
    } finally {
      unlockOutcome();
    }
  };

  // "Mark as scheduled again" on a CANCELLED class — the way back once the
  // Undo toast has gone. Retakes the pack credit when one is available.
  const doUnCancel = async () => {
    if (!lockOutcome()) return;
    try {
      const { data } = await api.post(`/sessions/${s.session_id}/restore`);
      if (data?.pack_coverage_dropped) {
        toast.success(
          "Restored. No pack credit was available, so this class will be invoiced when it is completed.",
          { duration: 10000 },
        );
      } else if (data?.pack_credit_taken_back) {
        toast.success("Back to scheduled — a pack credit was used again");
      } else {
        toast.success("Back to scheduled — cancellation removed");
      }
      onOpenChange(false);
      await reload();
    } catch (e) {
      toast.error(apiError(e, "Couldn't restore this class"));
    } finally {
      unlockOutcome();
    }
  };

  const doCancel = async (who) => {
    setPendingCancel(false);
    if (!lockOutcome()) return;
    try {
      await api.put(`/sessions/${s.session_id}/status`, { status: "cancelled", cancelled_by: who });
      onOpenChange(false);
      await reload();
      toast.success(`Cancelled — logged as ${who === "client" ? "client" : "you"} cancelled`, {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const { data } = await api.post(`/sessions/${s.session_id}/restore`);
              toast.success(
                data?.pack_coverage_dropped
                  ? "Restored. No pack credit was available, so this class will be invoiced when it is completed."
                  : "Cancellation undone — session restored",
              );
              await reload();
            } catch {
              toast.error("Couldn't undo — please try again");
            }
          },
        },
      });
    } catch (e) {
      toast.error(apiError(e, "Could not cancel session"));
    } finally {
      unlockOutcome();
    }
  };

  const doDelete = async () => {
    setPendingDelete(false);
    if (!lockOutcome()) return;
    try {
      const { data } = await api.delete(`/sessions/${s.session_id}`);
      toast.success(
        data?.pack_credit_refunded ? "Session deleted — pack credit returned" : "Session deleted",
      );
      onOpenChange(false);
      await reload();
    } catch (e) {
      toast.error(apiError(e, "Could not delete"));
    } finally {
      unlockOutcome();
    }
  };

  const openResched = () => {
    const d = new Date(s.starts_at);
    setReschedDate(toDateInput(d));
    setReschedTime(toTimeInput(d));
    setReschedWho(null);
    setReschedScope("single");
    setReschedOpen(true);
  };

  const isSeries = !!s.series_id;

  const doReschedule = async () => {
    if (!reschedWho) {
      toast.error("Choose who asked for the change");
      return;
    }
    if (!reschedDate || !reschedTime) {
      toast.error("Set a new date & time");
      return;
    }
    if (isOutsideWindow(reschedTime, s.duration_min)) {
      toast.error(BOOKING_WINDOW.message);
      return;
    }
    setReschedBusy(true);
    try {
      const startsIso = new Date(`${reschedDate}T${reschedTime}:00`).toISOString();
      const { data } = await api.patch(`/sessions/${s.session_id}/reschedule`, {
        starts_at: startsIso,
        apply_to_series: isSeries && reschedScope === "all",
        rescheduled_by: reschedWho,
      });
      const when = new Date(startsIso).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
      const movedCount = (data?.moved || []).length;
      const skipped = (data?.skipped || []).length;
      if (isSeries && reschedScope === "all") {
        toast.success(`Series moved — ${movedCount} session${movedCount === 1 ? "" : "s"}` + (skipped ? ` · ${skipped} skipped (clash)` : ""));
      } else {
        toast.success(`Rescheduled to ${when}`);
      }
      setReschedOpen(false);
      onOpenChange(false);
      await reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not reschedule");
    } finally {
      setReschedBusy(false);
    }
  };

  return (
    <>
      <Dialog open={!!session} onOpenChange={(o) => !o && onOpenChange(false)}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="session-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">Session details</DialogTitle>
            <DialogDescription className="sr-only">View, update status, or delete the selected training session.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[#2A2623]">
              <div><span className="text-[#6B635C]">Client:</span> <span className="font-bold">{name}</span></div>
              <div><span className="text-[#6B635C]">When:</span> <span data-testid="session-edit-when">{fmtMelFull(s.starts_at)}</span></div>
              <div><span className="text-[#6B635C]">Duration:</span> {s.duration_min} min · {s.session_type}</div>
              <div><span className="text-[#6B635C]">Status:</span> <span className={`font-bold uppercase ${isCancelled ? "text-[#2A2623]" : isNoShow ? "text-[#A8372A]" : "text-[#0F6B6B]"}`}>{s.status}</span></div>
              {billing && !billing.packReturned && (
                <div className="mt-2" data-testid="session-edit-billing">
                  <span className="text-[#6B635C] text-xs mr-2">Billing:</span>
                  <BillingChip billing={billing} sessionId={s.session_id} variant="full" />
                </div>
              )}
              {billing?.kind === "pack" && (
                <div className="mt-2 text-xs text-[#1F5C8F]/90" data-testid="session-edit-pack-line">
                  {isCancelled && s.pack_refunded ? (
                    <span className="text-[#6B635C]">Pack credit returned.</span>
                  ) : (
                    <>
                      Covered by your {billing.packName || "pack"}
                      {billing.packTotal ? ` (${billing.packRemaining} of ${billing.packTotal} left)` : ""}.
                      {billing.packInvoiceNumber ? ` Pack invoice ${billing.packInvoiceNumber}, paid up-front.` : ""}
                    </>
                  )}
                </div>
              )}
              {invoice && (
                <div className="mt-2" data-testid="session-edit-invoice-row">
                  <span className="text-[#6B635C]">Invoice:</span>{" "}
                  <span className="font-bold text-[#0F6B6B]">{invoice.invoice_number}</span>
                  <span className="text-[#6B635C]"> · AUD ${Number(invoice.total).toFixed(2)} · </span>
                  <span className="uppercase tracking-wider text-[10px] font-bold text-[#0F6B6B]">{invoice.status}</span>
                </div>
              )}
              {s.notes && <div className="mt-2 text-xs text-[#6B635C]">{s.notes}</div>}
            </div>

            {readOnly && (
              <p className="text-xs text-[#7A5E18]/90 bg-[#FBF4DD] border border-[#ECDCA6] rounded-md px-3 py-2" data-testid="session-readonly-note">
                Your account is read only. Subscribe to keep working.
              </p>
            )}

            {isScheduled && (
              <div className={`grid gap-2 pt-2 ${hasStarted ? "grid-cols-3" : "grid-cols-1"}`}>
                {/* An outcome can only be recorded once the class has started. */}
                {hasStarted && (
                  <Button onClick={doComplete} disabled={outcomeLocked || readOnly} title={readOnly ? "Your account is read only" : undefined} data-testid="mark-completed" className="bg-[#0F6B6B]/15 text-[#0F6B6B] hover:bg-[#0F6B6B]/25 border border-[#0F6B6B]/30 disabled:opacity-40">
                    <CheckCircle2 className="w-4 h-4" /> Complete
                  </Button>
                )}
                {hasStarted && (
                  <Button onClick={doNoShow} disabled={outcomeLocked || readOnly} title={readOnly ? "Your account is read only" : undefined} data-testid="mark-noshow" className="bg-[#F9E4DF] text-[#A8372A] hover:bg-[#F3D6D0] border border-[#F0C9C1] disabled:opacity-40">
                    <XCircle className="w-4 h-4" /> No-show
                  </Button>
                )}
                <Button onClick={() => setPendingCancel(true)} disabled={outcomeLocked || readOnly} title={readOnly ? "Your account is read only" : undefined} data-testid="mark-cancelled" className="bg-[#EEE9E0] text-[#2A2623] hover:bg-[#EEE9E0] border border-[#C9BFAF]/30 disabled:opacity-40">
                  <Ban className="w-4 h-4" /> Cancel
                </Button>
              </div>
            )}

            {isScheduled && !hasStarted && (
              <p className="text-[11px] text-[#6B635C]" data-testid="outcome-not-started-hint">
                Complete and No-show: available once the class has started.
              </p>
            )}

            {/* Change outcome — a no-show can go back to scheduled while it
                carries no invoice. Same reversal as the Undo toast: status and
                reliability only, no credit movement, no invoice. */}
            {isNoShow && (
              <div className="pt-2" data-testid="noshow-change-outcome">
                <p className="text-[10px] uppercase tracking-widest text-[#6B635C] mb-1">Change outcome</p>
                {hasInvoice ? (
                  <p className="text-xs text-[#7A5E18]/90" data-testid="noshow-reverse-blocked">
                    This no-show has an invoice. Delete the invoice first.
                  </p>
                ) : (
                  <Button
                    onClick={doUnNoShow}
                    disabled={busy || readOnly}
                    title={readOnly ? "Your account is read only" : undefined}
                    data-testid="noshow-mark-scheduled"
                    className="w-full bg-[#0F6B6B]/15 text-[#0F6B6B] hover:bg-[#0F6B6B]/25 border border-[#0F6B6B]/30 disabled:opacity-40"
                  >
                    <RotateCcw className="w-4 h-4" /> Mark as scheduled again
                  </Button>
                )}
              </div>
            )}

            {/* A completed class can go back to scheduled while its invoice is
                unpaid and has never been emailed. Paid invoices are never
                touched. */}
            {isCompleted && (
              <div className="pt-2" data-testid="completed-change-outcome">
                <p className="text-[10px] uppercase tracking-widest text-[#6B635C] mb-1">Change outcome</p>
                {undoBlock ? (
                  <p className="text-xs text-[#7A5E18]/90" data-testid="undo-complete-blocked">
                    {undoBlock}
                  </p>
                ) : (
                  <Button
                    onClick={doUnComplete}
                    disabled={busy || readOnly}
                    title={readOnly ? "Your account is read only" : undefined}
                    data-testid="undo-complete"
                    className="w-full bg-[#0F6B6B]/15 text-[#0F6B6B] hover:bg-[#0F6B6B]/25 border border-[#0F6B6B]/30 disabled:opacity-40"
                  >
                    <RotateCcw className="w-4 h-4" /> Undo complete
                  </Button>
                )}
              </div>
            )}

            {/* A cancelled class stays in the client's Sessions tab; this is
                the way back to scheduled once the Undo toast has gone. */}
            {isCancelled && (
              <div className="pt-2" data-testid="cancelled-change-outcome">
                <p className="text-[10px] uppercase tracking-widest text-[#6B635C] mb-1">Change outcome</p>
                <Button
                  onClick={doUnCancel}
                  disabled={busy || readOnly}
                  title={readOnly ? "Your account is read only" : undefined}
                  data-testid="cancelled-mark-scheduled"
                  className="w-full bg-[#0F6B6B]/15 text-[#0F6B6B] hover:bg-[#0F6B6B]/25 border border-[#0F6B6B]/30 disabled:opacity-40"
                >
                  <RotateCcw className="w-4 h-4" /> Mark as scheduled again
                </Button>
                {s.cancelled_by && (
                  <p className="text-[11px] text-[#6B635C] mt-1" data-testid="cancelled-by-line">
                    Logged as {s.cancelled_by === "client" ? "client" : "you"} cancelled.
                  </p>
                )}
              </div>
            )}

            {isScheduled && (
              <Button
                onClick={openResched}
                disabled={busy || readOnly}
                title={readOnly ? "Your account is read only" : undefined}
                data-testid="session-reschedule"
                className="w-full bg-[#1F5C8F]/15 text-[#1F5C8F] hover:bg-[#1F5C8F]/25 border border-[#B9D4EC] disabled:opacity-40"
              >
                <CalendarClock className="w-4 h-4" /> Reschedule
              </Button>
            )}

            {/* Secondary delete — available for every status so a cancelled
                session (hidden from the grid, opened from the drawer) can be
                removed, and a mis-booked session can be scrubbed entirely. */}
            <p className="text-xs text-[#6B635C] pt-1">
              Wrong date or booked by mistake?{" "}
              <button
                type="button"
                onClick={() => setPendingDelete(true)}
                disabled={busy || readOnly}
                title={readOnly ? "Your account is read only" : undefined}
                data-testid="session-delete"
                className="text-[#A8372A] underline underline-offset-2 hover:text-[#A8372A] font-medium disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed disabled:hover:text-[#A8372A]"
              >
                Delete
              </button>
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)} data-testid="session-edit-close" className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={pendingCancel} onOpenChange={(o) => !o && setPendingCancel(false)}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="cancel-who-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623]">Who cancelled?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C]">
              {`${name}, ${fmtMel(s.starts_at)}. `}
              Kept for your records only — cancellations never change the reliability score.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Button
              data-testid="cancel-by-client"
              onClick={() => doCancel("client")}
              className="h-11 bg-[#FBF4DD] text-[#7A5E18] hover:bg-[#F6ECC9] border border-[#ECDCA6]"
            >
              Client cancelled
            </Button>
            <Button
              data-testid="cancel-by-trainer"
              onClick={() => doCancel("trainer")}
              className="h-11 bg-[#E1EDF7] text-[#1F5C8F] hover:bg-[#D3E4F4] border border-[#B9D4EC]"
            >
              I cancelled
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-who-dismiss"
              className="h-11 border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]"
            >
              Keep session
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingDelete} onOpenChange={(o) => !o && setPendingDelete(false)}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="session-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623]">Delete session?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C]">
              Delete this session with {name} on {fmtMelFull(s.starts_at)}? This can't be undone, and it also removes the session from your cancellation and reschedule counts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="session-delete-cancel"
              className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="session-delete-confirm"
              className="bg-[#A8372A] hover:bg-[#8E2E23] text-[#FFFFFF]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reschedule — date/time picker + "Who asked?" + (for series) scope.
          Runs the same conflict check server-side; a clash returns 409 and the
          move is blocked. A reschedule keeps the session SCHEDULED and its
          pack credit, and is never counted as a cancellation. */}
      <Dialog open={reschedOpen} onOpenChange={(o) => !o && !reschedBusy && setReschedOpen(false)}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-[#1F5C8F]" /> Reschedule
            </DialogTitle>
            <DialogDescription className="text-[#6B635C]">
              Move {name}'s session. Keeps it scheduled, keeps the pack credit, and never counts as a cancellation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="min-w-0">
              <label className="text-[10px] uppercase tracking-widest text-[#6B635C]">Date</label>
              <Input type="date" value={reschedDate} onChange={(e) => setReschedDate(e.target.value)} data-testid="reschedule-date" className="w-full min-w-0 bg-[#FAF7F2] border-[#E8E1D6] mt-1" />
            </div>
            <TimeGrid
              testid="reschedule-time"
              value={reschedTime}
              dateIso={reschedDate}
              durationMin={s.duration_min}
              onChange={setReschedTime}
              label="New start time"
            />

            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6B635C]">Who asked?</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Button
                  type="button"
                  data-testid="reschedule-by-client"
                  aria-pressed={reschedWho === "client"}
                  onClick={() => setReschedWho("client")}
                  className={reschedWho === "client" ? RESCHED_SELECTED : RESCHED_UNSELECTED}
                >
                  Client asked
                </Button>
                <Button
                  type="button"
                  data-testid="reschedule-by-trainer"
                  aria-pressed={reschedWho === "trainer"}
                  onClick={() => setReschedWho("trainer")}
                  className={reschedWho === "trainer" ? RESCHED_SELECTED : RESCHED_UNSELECTED}
                >
                  I did
                </Button>
              </div>
            </div>

            {isSeries && (
              <div data-testid="reschedule-scope">
                <label className="text-[10px] uppercase tracking-widest text-[#6B635C] flex items-center gap-1">
                  <Repeat className="w-3 h-3 text-[#0F6B6B]" /> Recurring session
                </label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <Button
                    type="button"
                    data-testid="reschedule-scope-single"
                    aria-pressed={reschedScope === "single"}
                    onClick={() => setReschedScope("single")}
                    className={reschedScope === "single" ? RESCHED_SELECTED : RESCHED_UNSELECTED}
                  >
                    Just this one
                  </Button>
                  <Button
                    type="button"
                    data-testid="reschedule-scope-all"
                    aria-pressed={reschedScope === "all"}
                    onClick={() => setReschedScope("all")}
                    className={reschedScope === "all" ? RESCHED_SELECTED : RESCHED_UNSELECTED}
                  >
                    This & all future
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:items-center">
            {!reschedWho && (
              <span data-testid="reschedule-who-hint" className="text-[11px] text-[#7A5E18]/90 sm:mr-auto">
                Choose who asked to continue
              </span>
            )}
            <Button
              variant="outline"
              onClick={() => setReschedOpen(false)}
              disabled={reschedBusy}
              data-testid="reschedule-cancel-btn"
              className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]"
            >
              Cancel
            </Button>
            <Button
              onClick={doReschedule}
              disabled={reschedBusy || !reschedWho}
              data-testid="reschedule-save"
              className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold disabled:opacity-50"
            >
              {reschedBusy ? "Moving…" : "Save new time"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
